import { RepeatInterval } from '../common/shared-dtos-scheduler-common';

// DB module format
export interface GeneralModule {
  allowChangeByOtherUsers: boolean;
  display: GeneralDisplaySection;
  advanced: GeneralAdvancedSection;
  securityTelemetry: GeneralSecurityTelemetrySection;
  communication: GeneralCommunicationSection;
  update: GeneralUpdateSection;
}

export interface GeneralUpdateSection {
  settings: GeneralUpdateSettings;
  updateLocations: GeneralUpdateLocation[];
}

export interface GeneralUpdateLocation {
  server: string;
  useProxy: boolean;
  useP2P?: boolean;
}

export enum GeneralUpdateLocationPlaceholder {
  ARRAKIS = '/ARRAKIS/',
  RELAY = '/RELAY/',
}

export interface GeneralUpdateSettings {
  proxy: {
    enable: boolean;
    profile: number;
    settings: {
      server: string;
      port: number;
      username: string;
      password: string;
    };
  };
  productUpdateScheduler: GeneralProductUpdateSettingsScheduler;
  signatureUpdateScheduler: GeneralSignatureUpdateSettingsScheduler;
  updateRing: GeneralUpdateRing;
  defaultLocation: boolean;
}

export enum GeneralUpdateRing {
  SLOW_RING = 0,
  FAST_RING = 1,
  SUPER_FAST_RING = 2,
  PRODUCTION_RING = 3,
  TEST1_RING = 4,
  TEST2_RING = 5,
}

export interface GeneralSignatureUpdateSettingsScheduler {
  enabled: boolean;
  occurrence: RepeatInterval;
  updateInterval: number;
  weekDays: {
    days: WeekDays[];
    startHour: number;
    startMinute: number;
    endHour: number;
    endMinute: number;
  };
}

export interface GeneralProductUpdateSettingsScheduler extends GeneralSignatureUpdateSettingsScheduler {
  postponeReboot: boolean;
  rebootAfterInstalling: {
    enable: boolean;
    settings: {
      day: GeneralUpdateSettingsSchedulerRebootDay;
      hour: number;
      minutes: number;
    };
  };
}

export enum GeneralUpdateSettingsSchedulerRebootDay {
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6,
  SUNDAY = 0,
  EVERY_DAY = 7,
}

export interface GeneralDisplaySection {
  silentMode: GeneralDisplaySilentMode;
  supportInformation: {
    website: string;
    email: string;
    phone: string;
  };
}

export interface GeneralDisplaySilentMode {
  enable: boolean;
  iconNotificationArea: boolean;
  showPopups: GeneralDisplaySilentModeShowPopups;
  displayAlertsPopups: boolean;
  showBrowserToolbar: boolean;
  issuesVisibility: GeneralDisplaySilentModeIssuesVisibility;
  endpointRestartPopup: GeneralDisplaySilentModeEndpointRestartPopup;
}

export interface GeneralDisplaySilentModeEndpointRestartPopup {
  enable: boolean;
  update: boolean;
  patchManagement: boolean;
}
export interface GeneralDisplaySilentModeIssuesVisibility {
  enable: boolean;
  profile: number;
  general: boolean;
  installationRestartNotifications: GeneralDisplaySilentModeIssuesVisibilityAlertAction;
  antimalware: boolean;
  onAccessNotifications: GeneralDisplaySilentModeIssuesVisibilityAlertAction;
  onDemandNotifications: GeneralDisplaySilentModeIssuesVisibilityAlertAction;
  showAfter: number;
  dissinfectionRestartNotification: GeneralDisplaySilentModeIssuesVisibilityAlertAction;
  firewall: boolean;
  contentControl: boolean;
  update: boolean;
  updateRestartNotifications: GeneralDisplaySilentModeIssuesVisibilityAlertAction;
  cloudConnectionNotifications: GeneralDisplaySilentModeIssuesVisibilityAlertAction;
}
export enum GeneralDisplaySilentModeIssuesVisibilityAlertAction {
  DO_NOT_SHOW = 1,
  SHOW_AS_WARNING = 2,
  SHOW_AS_CRITICAL = 3,
}
export interface GeneralDisplaySilentModeShowPopups {
  enable: boolean;
  settings: GeneralDisplaySilentModeShowPopupsSettings;
}
export interface GeneralDisplaySilentModeShowPopupsSettings {
  general: GeneralDisplaySilentModeShowPopupsSettingsLevel;
  update: GeneralDisplaySilentModeShowPopupsSettingsLevel;
  antimalware: GeneralDisplaySilentModeShowPopupsSettingsLevel;
  sandboxAnalyzer?: GeneralDisplaySilentModeShowPopupsSettingsLevel;
  firewall: GeneralDisplaySilentModeShowPopupsSettingsLevel;
  contentControl: GeneralDisplaySilentModeShowPopupsSettingsLevel;
  deviceControl: GeneralDisplaySilentModeShowPopupsSettingsLevel;
  exchange?: GeneralDisplaySilentModeShowPopupsSettingsLevel;
  encryption?: GeneralDisplaySilentModeShowPopupsSettingsLevel;
  applicationControl?: GeneralDisplaySilentModeShowPopupsSettingsLevel;
}
export interface GeneralDisplaySilentModeShowPopupsSettingsLevel {
  info: boolean;
  low: boolean;
  critical: boolean;
}

export interface GeneralAdvancedSection {
  passwordConfig: { profile: number; value: string };
  powerUser: { enabled: boolean; password: string };
  settings: {
    scanSsl: GeneralAdvancedSettingsScanSsl;
    excludeFinanceDomains: boolean;
    mitm: {
      enabled: boolean;
      processes: string[];
    };
    scanClientHello: {
      enabled: boolean;
      blockType: GeneralAdvancedScanClientHelloBlockType;
    };
    browserSearchAdvisor: boolean;
    removeEvents: { settings: { days: number } };
    reportActiveSessions: boolean;
    sendTelemetry: boolean;
    submitReports: boolean;
    submitSuspicious: boolean;
    useGlobalProtectiveNetwork: boolean;
  };
}

export interface GeneralAdvancedSettingsScanSsl {
  enabled: boolean;
  protocols: {
    incoming: {
      rdp: boolean;
    };
    outgoing: {
      http: boolean;
      ftp: boolean;
      ssh: boolean;
    };
  };
}

export enum GeneralAdvancedScanClientHelloBlockType {
  INSERT_ERROR_PAGE = 1,
  RESET_CONNECTION = 2,
}

export interface GeneralSecurityTelemetrySection {
  enabled: boolean;
  allowSelfSignedCertificate: boolean;
  siem: SecurityTelemetrySiemType;
  protocol: SecurityTelemetryProtocol;
  format: string | number;
  proxy: SecurityTelemetryProxy;
  eventTypes: SecurityTelemetryEventTypes;
  config: SecurityTelemetryConfig;
}

export interface SecurityTelemetryProxy {
  profile: SecurityTelemetryProxyType;
}
export interface SecurityTelemetryEventTypes {
  createFile: boolean;
  createProcess: boolean;
  deleteFile: boolean;
  logon: boolean;
  logout: boolean;
  modifyFile: boolean;
  moveFile: boolean;
  networkConnection: boolean;
  readFromFile: boolean;
  regCreateKey: boolean;
  regDeleteKey: boolean;
  regDeleteValue: boolean;
  regModifyValue: boolean;
  terminateProcess: boolean;
}
export interface SecurityTelemetryConfig {
  hostname?: string;
  port?: number;
  ignoreSSLErrors?: boolean;
  key: string;
  url: string;
}
export enum SecurityTelemetrySiemType {
  NONE = 0,
  SPLUNK = 1,
  SYSLOG = 3,
}

export enum SecurityTelemetryUrlValidity {
  INVALID_FAILED = -1,
  INVALID_TLS = 0,
  VALID = 1,
}

export enum SecurityTelemetryProxyType {
  ENABLE = 2,
  DISABLE = 3,
}
export enum SecurityTelemetryProtocol {
  HTTP = 1,
  TLS = 2,
}

export enum EcsAssignmentType {
  ECS_ASSIGNMENT_TYPE_CLOUD_DEFAULT = -1,
  ECS_ASSIGNMENT_TYPE_ECS = 0,
  ECS_ASSIGNMENT_TYPE_SUPA = 1,
}

export const FORMAT_TYPE_JSON = 1;

export interface GeneralCommunicationSection {
  cloudServicesProxy: {
    profile: CommunicationProxyType;
  };
  ecsAssignments: EcsAssignment[];
  ecsProxy: {
    profile: CommunicationProxyType;
  };
  remoteShell: {
    enabled: boolean;
  };
}

export enum CommunicationProxyType {
  KEEP_CURRENT_SETTINGS = 1,
  ENABLE_PROXY = 2,
  DISABLE_PROXY = 3,
  AUTO_DETECT_PROXY = 4,
}

export interface EcsAssignment {
  customHostname: string;
  ip: string;
  name: string;
  protectedEntityId: string;
  type: EcsAssignmentType;
  url: string;
}

export interface EcsAssignmentFormValue extends EcsAssignment {
  defaultPositionInGrid: number;
}

export interface CommunicationServerMachineAndSUPA {
  customHostname: string;
  ip: string;
  name: string;
  type: EcsAssignmentType;
  url: string;
  _id: string;
}

export interface CommunicationServerMachineAndSUPARequest {
  page: number;
  limit: number;
  filters: RequestFilter[];
}

export interface RequestFilter {
  property: string;
  value: string;
}
export enum PasswordConfigProfile {
  KEEP_CURRENT_SETTINGS = 1,
  ENABLE_PASSWORD = 2,
  DISABLE_PASSWORD = 3,
}

export enum WeekDays {
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
  Sunday = 0,
}
